/** Mon–Sun toggles shared by every day picker; amber for selected, like the primary action. */
import React from "react"

import { DAY_LABELS, DAY_NAMES, DAY_SHORT_LABELS } from "@/lib/collectionCoverage"
import { cn } from "@/lib/utils"
import { DayOfWeek } from "@/types/collections"

interface DayToggleGroupProps {
  value: DayOfWeek[]
  onChange: (days: DayOfWeek[]) => void
  className?: string
  disabled?: boolean
}

const DayToggleGroup: React.FC<DayToggleGroupProps> = ({
  value,
  onChange,
  className,
  disabled,
}) => {
  const toggle = (day: DayOfWeek) =>
    onChange(value.includes(day) ? value.filter((d) => d !== day) : [...value, day])

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {DAY_NAMES.map((day) => {
        const selected = value.includes(day)
        return (
          <button
            key={day}
            type="button"
            disabled={disabled}
            onClick={() => toggle(day)}
            aria-pressed={selected}
            aria-label={DAY_LABELS[day]}
            className={cn(
              "rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-75",
              selected
                ? "border-amber-300 bg-amber-100 text-amber-800"
                : "border-palette-jungleGreen200 bg-white text-palette-jungleGreen600 hover:bg-palette-jungleGreen000",
            )}
          >
            {DAY_SHORT_LABELS[day]}
          </button>
        )
      })}
    </div>
  )
}

export default DayToggleGroup

/** Read-only fixed-width day strip; the letters are positional and aria-hidden, the label carries the meaning. */
export const DayStrip: React.FC<{ days: DayOfWeek[]; className?: string }> = ({
  days,
  className,
}) => {
  const active = DAY_NAMES.filter((day) => days.includes(day))
  return (
    <span
      className={cn("inline-flex shrink-0 gap-px", className)}
      aria-label={
        active.length === 0
          ? "No days"
          : active.length === 7
            ? "Every day"
            : active.map((day) => DAY_LABELS[day]).join(", ")
      }
    >
      {DAY_NAMES.map((day) => (
        <span
          key={day}
          aria-hidden="true"
          className={cn(
            "w-3.5 text-center text-[10px] font-medium leading-4",
            days.includes(day)
              ? "text-palette-jungleGreen900"
              : "text-palette-jungleGreen300",
          )}
        >
          {DAY_SHORT_LABELS[day][0]}
        </span>
      ))}
    </span>
  )
}
